/**
 * Which corner the PIP stands in, decided by counting bars rather than guessing.
 *
 * A panel dropped in a fixed corner covers price action half the time. The rule
 * people apply by eye is "put it where the chart is empty", and on a price chart
 * that is nearly always a *left* corner: the line enters low-left in an uptrend
 * and high-left in a downtrend, so one of the two is free whichever way it runs.
 *
 * ## Only the left column is a candidate, and that is not a simplification
 *
 * The other two corners are already spoken for by things this app draws:
 *
 * | corner | occupant |
 * |---|---|
 * | top-right | the feed badge, `chart error:` and the refusal notice, in one column |
 * | bottom-right | the chart's own controls — history preview, data inspector |
 * | right edge, full height | the Y axis and its labels |
 *
 * So the choice is two boxes, not four, and adding the right-hand pair would
 * mean deciding which of those to cover.
 *
 * ## Counting, not slope
 *
 * The slope is the *usual* answer and it is not the general one. A chart that
 * rises, falls back and ends flat has no slope worth reading, and a series with
 * one spike has a slope that describes the spike rather than the chart. Counting
 * how many bars actually fall inside each candidate box answers the question
 * that was being asked — "is anything drawn here" — for every shape, including
 * the ones a slope gets backwards.
 *
 * The slope is kept as the **tie-break**, because a tie is exactly the case
 * where counting has nothing to say: two empty corners, or two equally busy
 * ones. Then the conventional reading is as good as anything and better than an
 * arbitrary constant.
 *
 * ## Closes, not highs and lows
 *
 * A candle's wick is a hairline and the eye does not read a panel over one as an
 * obstruction; the body and the line through the closes are what "the chart" is.
 * Counting every high and low would make a volatile series report both corners
 * as busy and fall through to the tie-break on charts where one corner is
 * plainly empty.
 *
 * Pure and DOM-free on purpose: the shell measures the rectangles and hands them
 * over, so every rule here is testable in Node, which nothing touching SciChart
 * can be.
 */

/** A rectangle in the chart container's own pixel space. */
export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where the PIP may go. Narrower than the contract's `pipCorner`, which also
 * carries `auto` and the two right-hand corners a user may force — this is the
 * set *this function* chooses between, and widening it would mean covering the
 * axis or the badges above.
 */
export type PlacedCorner = "topLeft" | "bottomLeft";

/** How many of the plotted closes fall inside `box`. */
export function occupancy(closes: readonly number[], plot: Box, box: Box): number {
  const points = plotPoints(closes, plot);
  let n = 0;
  for (const p of points) {
    if (p.x >= box.x && p.x <= box.x + box.width && p.y >= box.y && p.y <= box.y + box.height) {
      n++;
    }
  }
  return n;
}

/**
 * Each close as a point in the plot rectangle.
 *
 * Bars are evenly spaced across the width — which is what a category-collapsed
 * axis draws — and the value range is mapped to the full height. A flat series
 * has no range to map, so it sits on the vertical centre rather than dividing by
 * zero; that is also the case the tie-break is for.
 */
function plotPoints(closes: readonly number[], plot: Box): { x: number; y: number }[] {
  if (closes.length === 0) return [];
  let lo = Infinity;
  let hi = -Infinity;
  for (const c of closes) {
    if (!Number.isFinite(c)) continue;
    if (c < lo) lo = c;
    if (c > hi) hi = c;
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [];

  const span = hi - lo;
  const step = closes.length > 1 ? plot.width / (closes.length - 1) : 0;
  const out: { x: number; y: number }[] = [];
  closes.forEach((c, i) => {
    if (!Number.isFinite(c)) return;
    // A zero span is a flat line, which belongs in the middle of the pane rather
    // than at its top: the renderer pads a degenerate range the same way.
    const t = span === 0 ? 0.5 : (c - lo) / span;
    out.push({ x: plot.x + i * step, y: plot.y + plot.height - t * plot.height });
  });
  return out;
}

/**
 * Which way the series runs, from the first and last finite close.
 *
 * First-and-last rather than a regression: this is a tie-break between two
 * corners, and the extra precision would change the answer only where the two
 * disagree, which is the case that already fell through to here.
 */
export function trend(closes: readonly number[]): "up" | "down" | "flat" {
  const finite = closes.filter((c) => Number.isFinite(c));
  if (finite.length < 2) return "flat";
  const first = finite[0]!;
  const last = finite[finite.length - 1]!;
  if (last > first) return "up";
  if (last < first) return "down";
  return "flat";
}

/**
 * The corner to put the PIP in.
 *
 * `topLeft` and `bottomLeft` are the boxes the panel *would* occupy, already
 * offset by whatever sits above them — the caller subtracts the value legend's
 * measured height, because only it knows how many rows the legend drew.
 *
 * Ties go to the trend: a rising chart leaves its top-left empty, a falling one
 * its bottom-left. A flat or unreadable series gets `topLeft`, which is where
 * the panel sits under the legend and out of the way of the last bar.
 */
export function placePip(
  closes: readonly number[],
  plot: Box,
  topLeft: Box,
  bottomLeft: Box,
): PlacedCorner {
  const top = occupancy(closes, plot, topLeft);
  const bottom = occupancy(closes, plot, bottomLeft);
  if (top !== bottom) return top < bottom ? "topLeft" : "bottomLeft";
  return trend(closes) === "down" ? "bottomLeft" : "topLeft";
}

/** Clear of the x axis labels, so the bottom-left panel does not sit on them. */
export const BOTTOM_INSET = 34;
export const SIDE_INSET = 8;
/** Below the legend block, with the same 10px the mock settled on. */
const LEGEND_GAP = 10;
/** The legend block's own inset from its pane's top edge (`ValueLegend`'s). */
const LEGEND_INSET = 8;
/** Nothing useful is drawn below this, whatever the size fraction says. */
const MIN_W = 168;
const MIN_H = 112;

/** The PIP's size and the rectangles `placePip` chooses between, in pixels. */
export interface PipFrame {
  width: number;
  height: number;
  plot: Box;
  topLeft: Box;
  bottomLeft: Box;
}

/**
 * The price pane's rectangle, and the two boxes the PIP may take inside it.
 *
 * **Measured from the price pane's top, not the chart's** (2026-10-20). A pane
 * stacked above price — a signal lane like Bongo — pushes the price legend down
 * by its height, and a PIP placed at `legend height` from the chart's top edge
 * sat on that legend. `bandTop` and `bandBottom` are fractions (0–1) of the
 * container's height, `stackFractions`' units.
 */
export function pipFrame(
  container: { width: number; height: number },
  bandTop: number,
  bandBottom: number,
  legendHeight: number,
  fraction: number,
): PipFrame {
  const top = container.height * bandTop;
  const paneH = container.height * (bandBottom - bandTop);
  const width = Math.max(MIN_W, Math.round(container.width * fraction));
  const height = Math.max(MIN_H, Math.round(paneH * fraction));
  const topY = top + LEGEND_INSET + legendHeight + LEGEND_GAP;
  const bottomY = top + paneH - BOTTOM_INSET - height;
  return {
    width,
    height,
    plot: { x: 0, y: top, width: container.width, height: paneH },
    topLeft: { x: SIDE_INSET, y: topY, width, height },
    bottomLeft: { x: SIDE_INSET, y: bottomY, width, height },
  };
}
